import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const getToken = () => localStorage.getItem('notes_token') || '';
const fetchWithAuth = (url, options = {}) => {
  const token = getToken();
  return fetch(url, { ...options, headers: { ...options.headers, 'x-token': token } });
};

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
}

export default function CompletedTaskMaintenance() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [maintainTask, setMaintainTask] = useState(null);
  const [maintainNotes, setMaintainNotes] = useState('');
  const [maintainFiles, setMaintainFiles] = useState([]);
  const [maintainUploading, setMaintainUploading] = useState(false);

  const fetchCompleted = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/projects/${id}/completed-tasks`);
      const data = await res.json();
      setTasks(data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchCompleted(); }, [id]);

  const downloadFile = (url, name) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.download = name || 'download';
    a.click();
  };

  const viewVersion = (task, versionId) => {
    const v = task.versions?.find(v => v.versionId === versionId);
    if (v) setSelectedVersion({ taskName: task.name, ...v });
  };

  // 打开任务维护
  const openMaintain = (task) => {
    setMaintainTask(task);
    setMaintainNotes('');
    setMaintainFiles([]);
  };

  // 保存任务维护（生成新版本）
  const saveMaintain = async () => {
    if (!maintainNotes.trim()) {
      alert('请填写工作说明');
      return;
    }
    setMaintainUploading(true);
    try {
      // 1. 上传附件
      const attachments = [];
      for (const file of maintainFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await fetchWithAuth('/api/upload', { method: 'POST', body: formData });
        const uploadData = await uploadRes.json();
        if (uploadData.url || uploadData.cosUrl) {
          attachments.push({
            id: uploadData.id || Date.now(),
            originalName: file.name,
            cosUrl: uploadData.cosUrl || uploadData.url,
            size: file.size,
            fileKey: uploadData.fileKey || ''
          });
        }
      }
      
      // 2. 更新任务（触发新的版本生成）
      const res = await fetchWithAuth(`/api/tasks/${maintainTask.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: '已完成',
          workNotes: maintainNotes,
          attachments: [...attachments]  // 只传新上传的附件，不复制旧附件
        })
      });
      const data = await res.json();
      // 后端返回的是任务对象，不是 { success: true }
      if (!data.error && data.id) {
        alert('版本已添加');
        setMaintainTask(null);
        fetchCompleted();
      } else {
        alert(data.error || '保存失败');
      }
    } catch (e) {
      alert('网络错误');
    }
    setMaintainUploading(false);
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => window.history.back()} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff', cursor: 'pointer', fontSize: 14 }}>← 返回</button>
          <h2 style={{ margin: 0 }}>✅ 已完成任务维护</h2>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">加载中...</div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">暂无已完成任务</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {tasks.map(task => (
            <div key={task.id} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8e8e8', padding: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12, color: '#333' }}>{task.name}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: '#f6ffed', color: '#52c41a' }}>已完成</span>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: '#f0f5ff', color: '#2f54eb' }}>负责人: {task.assignee}</span>
                {task.members && task.members.length > 0 && (
                  <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: '#f0f5ff', color: '#2f54eb' }}>成员: {task.members.join(', ')}</span>
                )}
                <button onClick={() => openMaintain(task)} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, border: '1px solid #1890ff', background: '#e6f7ff', color: '#1890ff', cursor: 'pointer' }}>✏️ 维护</button>
              </div>

              {/* 版本列表 */}
              {task.versions && task.versions.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>📌 版本记录</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {[...task.versions].sort((a, b) => b.versionId.localeCompare(a.versionId)).map(v => (
                      <div
                        key={v.versionId}
                        onClick={() => viewVersion(task, v.versionId)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                          background: '#fafafa', border: '1px solid #f0f0f0',
                          transition: 'all 0.2s'
                        }}
                      >
                        <span style={{ fontWeight: 500, color: '#1890ff', fontSize: 13, minWidth: 130 }}>
                          🔖 {v.versionId}
                        </span>
                        <span style={{ color: '#999', fontSize: 12 }}>
                          {v.completedAt?.replace('T', ' ').substring(0, 16)}
                        </span>
                        <span style={{ color: '#666', fontSize: 12 }}>完成人: {v.completedBy}</span>
                        {v.workNotes && (
                          <span style={{ color: '#999', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {v.workNotes.substring(0, 50)}{v.workNotes.length > 50 ? '...' : ''}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 无版本历史，显示当前数据 */}
              {(!task.versions || task.versions.length === 0) && task.workNotes && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                  <span style={{ color: '#666', fontSize: 13, whiteSpace: 'pre-wrap' }}>{task.workNotes}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 版本详情弹窗 */}
      {selectedVersion && (
        <div className="modal-overlay" onClick={() => setSelectedVersion(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, padding: 24 }}>
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e8e8e8' }}>
              <h3 style={{ margin: 0 }}>🔖 版本详情</h3>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: '#999', fontSize: 13 }}>任务：</span>
              <span style={{ fontWeight: 500, fontSize: 14 }}>{selectedVersion.taskName}</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: '#999', fontSize: 13 }}>版本号：</span>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#1890ff' }}>{selectedVersion.versionId}</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <span style={{ color: '#999', fontSize: 13 }}>完成人：</span>
              <span>{selectedVersion.completedBy}</span>
              <span style={{ color: '#999', marginLeft: 16, fontSize: 13 }}>完成时间：</span>
              <span>{selectedVersion.completedAt?.replace('T', ' ').substring(0, 16)}</span>
            </div>
            {selectedVersion.workNotes && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ color: '#333', fontSize: 14, marginBottom: 8 }}>📝 工作说明</div>
                <div style={{ padding: '10px 14px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: 13 }}>{selectedVersion.workNotes}</div>
              </div>
            )}
            {selectedVersion.attachments && selectedVersion.attachments.length > 0 && (
              <div>
                <div style={{ color: '#333', fontSize: 14, marginBottom: 8 }}>📎 附件</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {selectedVersion.attachments.map(att => (
                    <div key={att.id} style={{ padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => downloadFile(att.cosUrl, att.originalName)}>
                      <span style={{ color: '#1890ff', fontSize: 13 }}>📄 {att.originalName} <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{formatSize(att.size)}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button onClick={() => setSelectedVersion(null)} className="btn-secondary" style={{ padding: '6px 20px' }}>关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* 任务维护弹窗 */}
      {maintainTask && (
        <div className="modal-overlay" onClick={() => setMaintainTask(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, padding: 24 }}>
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e8e8e8' }}>
              <h3 style={{ margin: 0 }}>✏️ 任务维护 - {maintainTask.name}</h3>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#333', fontSize: 14, marginBottom: 8 }}>📝 工作说明 <span style={{ color: '#f5222d' }}>*</span></div>
              <textarea
                value={maintainNotes}
                onChange={e => setMaintainNotes(e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d9d9d9', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder="请填写工作说明..."
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#333', fontSize: 14, marginBottom: 8 }}>📎 附件</div>
              <input
                type="file"
                multiple
                onChange={e => setMaintainFiles([...e.target.files])}
                style={{ fontSize: 13 }}
              />
              {maintainFiles.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                  已选择 {maintainFiles.length} 个文件
                </div>
              )}
            </div>
            <div style={{ marginTop: 20, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setMaintainTask(null)} className="btn-secondary" style={{ padding: '6px 20px' }}>取消</button>
              <button onClick={saveMaintain} disabled={maintainUploading} className="btn-primary" style={{ padding: '6px 20px' }}>
                {maintainUploading ? '保存中...' : '💾 保存（生成新版本）'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
